import React, {
  createContext,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react'

import { Trans } from '@lingui/react/macro'
import { ViewProps, isWeb } from 'tamagui'

import { canHover } from './canHover'
import { Tooltip } from '../Tooltip'

import { Text, Paragraph } from '../Text'
import { Button } from '../Button/Button'
import { Link } from '../Link'
import { Icon } from '../Icon'
import { LogoIconAnimated } from '../Logo/LogoIconAnimated'

import { DynamicIslandHomeExpand } from './DynamicIslandHomeExpand'

import { DynamicIslandProfileButton } from './DynamicIslandProfileButton'
import { DynamicIslandProfileExpand } from './DynamicIslandProfileExpand'

export type Item = {
  id: string
  name?: string
  component:
    | React.ReactNode
    | (({ isLoading }: { isLoading: boolean }) => React.ReactNode)
  child?: React.ReactNode
  containerStyle?: ViewProps
  meta?: Record<string, unknown>
}

export const DynamicIslandRowsContext = createContext<{
  rows: Array<Array<Item>>
  setRows: React.Dispatch<React.SetStateAction<Array<Array<Item>>>>
  pushRows: (rows: Array<Array<Item>>, id: string) => void
  popRows: (id: string) => void
}>({
  rows: [],
  setRows: () => {},
  pushRows: () => {},
  popRows: () => {},
})

const isMac =
  isWeb &&
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad/.test(navigator.platform || '')
const searchHotkeyLabel = isMac ? '\u2318K' : 'Ctrl+K'

const SearchButton = () => (
  <Button
    key='search'
    minHeight='$8'
    height='$8'
    width='$8'
    padding='$0'
    alignItems='center'
    justifyContent='center'
    secondary
    onPress={() =>
      window.dispatchEvent(new CustomEvent('guild-open-search'))
    }
  >
    <Icon name='search' color='$color' />
  </Button>
)

const SearchButtonWithTooltip = () => {
  if (!canHover) {
    return <SearchButton />
  }

  return (
    <Tooltip placement='bottom' delay={300}>
      <Tooltip.Trigger tag='dialog'>
        <SearchButton />
      </Tooltip.Trigger>

      <Tooltip.Content
        enterStyle={{ x: 0, y: -5, opacity: 0, scale: 0.9 }}
        exitStyle={{ x: 0, y: -5, opacity: 0, scale: 0.9 }}
      >
        <Tooltip.Arrow />

        <Paragraph size='$4' color='$colorOnPrimary' fontWeight='$5'>
          {searchHotkeyLabel}
        </Paragraph>
      </Tooltip.Content>
    </Tooltip>
  )
}

export const DynamicIslandProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [dynamicIslandRows, setDynamicIslandRows] = useState<
    Array<Array<Item>>
  >(() => [
    [
      {
        id: 'logo',
        component: ({ isLoading }) => {
          return (
            <Link
              key='home'
              href='/'
              hoverStyle={{ textDecorationLine: 'none' }}
              width='$8'
              height='$8'
            >
              <LogoIconAnimated width='$8' height='$8' animate={isLoading} />
            </Link>
          )
        },
        containerStyle: {
          // marginRight: '$5',
          $gtXs: {
            marginRight: '$5',
          },
        },
      },
      {
        id: 'logo-word',
        component: (
          <Link key='home' href='/' hoverStyle={{ textDecorationLine: 'none' }}>
            <Text fontSize='$6' fontWeight='$7' color='$color'>
              Guild
            </Text>
          </Link>
        ),
        containerStyle: {
          display: 'flex',
          marginRight: '$3',

          $gtXs: {
            display: 'none',
          },
        },
      },
      {
        id: 'home-menu',
        name: 'Home',
        component: (
          <Button
            key='profile'
            minHeight='$8'
            height='$8'
            width='$8'
            padding='$0'
            alignItems='center'
            justifyContent='center'
            secondary
          >
            <Icon name='navigation' color='$color' />
          </Button>
        ),
        child: <DynamicIslandHomeExpand />,
        containerStyle: {
          display: 'flex',
          $gtXs: {
            display: 'none',
          },
        },
      },
      {
        id: 'guilds',
        component: (
          <Link
            key='guilds'
            href='/guilds'
            hoverStyle={{ textDecorationLine: 'none' }}
          >
            <Text color='$color'><Trans>Guilds</Trans></Text>
          </Link>
        ),
        containerStyle: {
          marginRight: '$1',
          display: 'none',
          $gtXs: {
            display: 'flex',
          },
        },
      },
      {
        id: 'events',
        component: (
          <Link
            key='events'
            href='/events'
            hoverStyle={{ textDecorationLine: 'none' }}
          >
            <Text color='$color'><Trans>Events</Trans></Text>
          </Link>
        ),
        containerStyle: {
          marginHorizontal: '$1',
          display: 'none',
          $gtXs: {
            display: 'flex',
          },
        },
      },
      {
        id: 'presentations',
        component: (
          <Link
            key='presentations'
            href='/presentations'
            hoverStyle={{ textDecorationLine: 'none' }}
          >
            <Text color='$color'><Trans>Presentations</Trans></Text>
          </Link>
        ),
        containerStyle: {
          marginLeft: '$1',
          marginRight: '$5',
          display: 'none',
          $gtXs: {
            display: 'flex',
          },
        },
      },
      {
        id: 'search',
        component: <SearchButtonWithTooltip />,
      },
      {
        id: 'profile',
        name: 'Profile',
        component: <DynamicIslandProfileButton />,
        child: <DynamicIslandProfileExpand />,
      },
    ],
  ])

  // Stack of pushed row layers. Each entry records an ID and the rows
  // it displays. The default nav-bar rows (from useState above) sit
  // underneath the stack and are restored when the stack empties.
  const rowStackRef = useRef<Array<{ id: string; rows: Array<Array<Item>> }>>(
    []
  )
  const defaultRowsRef = useRef(dynamicIslandRows)

  const pushRows = useCallback((rows: Array<Array<Item>>, id: string) => {
    // Remove any existing entry with the same ID to prevent duplicates
    rowStackRef.current = rowStackRef.current.filter((entry) => entry.id !== id)
    rowStackRef.current.push({ id, rows })
    setDynamicIslandRows(rows)
  }, [])

  const popRows = useCallback((id: string) => {
    const stack = rowStackRef.current
    const index = stack.findIndex((entry) => entry.id === id)
    if (index === -1) return

    const wasTop = index === stack.length - 1
    stack.splice(index, 1)

    // Only update display when the removed entry was on top
    if (wasTop) {
      if (stack.length > 0) {
        setDynamicIslandRows(stack[stack.length - 1].rows)
      } else {
        setDynamicIslandRows(defaultRowsRef.current)
      }
    }
  }, [])

  const contextValue = useMemo(() => {
    return {
      rows: dynamicIslandRows,
      setRows: setDynamicIslandRows,
      pushRows,
      popRows,
    }
  }, [dynamicIslandRows, setDynamicIslandRows, pushRows, popRows])

  return (
    <DynamicIslandRowsContext.Provider value={contextValue}>
      {children}
    </DynamicIslandRowsContext.Provider>
  )
}
